/* Zareen Jewels — collection page: live filters/sort (section rendering), load more, grid size, category rail, sticky toolbar offset */
(() => {
  const html = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const init = (root) => {
    if (!root || root.dataset.zjgReady) return;
    root.dataset.zjgReady = '1';
    const sectionId = root.dataset.section;
    const baseUrl = root.dataset.url;
    const drawer = root.querySelector('[data-zjg-drawer]');
    const form = root.querySelector('[data-zjg-form]');

    /* ---------- grid size (remembered per visitor) ---------- */
    const setDensity = (d) => {
      root.dataset.density = d;
      root.querySelectorAll('[data-zjg-density]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.zjgDensity === d)));
      try { localStorage.setItem('zjg-density', d); } catch (e) {}
    };
    let saved = '2';
    try { saved = localStorage.getItem('zjg-density') || '2'; } catch (e) {}
    setDensity(saved);

    /* ---------- category rail family switch ---------- */
    const cats = root.querySelector('[data-zjg-cats]');
    if (cats) {
      cats.querySelectorAll('[data-fam]').forEach((b) => b.addEventListener('click', () => {
        cats.dataset.active = b.dataset.fam;
        cats.querySelectorAll('[data-fam]').forEach((x) => x.setAttribute('aria-selected', String(x === b)));
      }));
    }

    /* ---------- sticky toolbar sits under the header when it is showing ---------- */
    const header = document.querySelector('.zj-header-section');
    if (header) {
      let raf = 0;
      const sync = () => {
        raf = 0;
        const b = header.getBoundingClientRect().bottom;
        root.style.setProperty('--zjg-top', `${Math.max(0, Math.round(b))}px`);
      };
      window.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(sync); }, { passive: true });
      new MutationObserver(() => setTimeout(sync, 380)).observe(header, { attributes: true, attributeFilter: ['class'] });
      sync();
    }

    /* ---------- drawer ---------- */
    let returnTo = null;
    const openDrawer = (opener) => {
      returnTo = opener;
      drawer.hidden = false;
      html.classList.add('zj-lock');
      root.querySelectorAll('[data-zjg-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      requestAnimationFrame(() => requestAnimationFrame(() => drawer.classList.add('is-open')));
      setTimeout(() => drawer.querySelector('.zjg-drawer__x').focus({ preventScroll: true }), 60);
    };
    const closeDrawer = () => {
      if (drawer.hidden) return;
      drawer.classList.remove('is-open');
      html.classList.remove('zj-lock');
      root.querySelectorAll('[data-zjg-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      setTimeout(() => { if (!drawer.classList.contains('is-open')) drawer.hidden = true; }, reduce ? 0 : 450);
      const opener = root.querySelector('[data-zjg-open]');
      (opener || returnTo) && (opener || returnTo).focus({ preventScroll: true });
    };
    drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); closeDrawer(); return; }
      if (e.key !== 'Tab') return;
      const f = [...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select')].filter((n) => n.offsetParent !== null);
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    });

    /* ---------- fetching (section rendering API) ---------- */
    let ctrl = null;
    const withSection = (url) => url + (url.includes('?') ? '&' : '?') + `section_id=${encodeURIComponent(sectionId)}`;
    const cleanUrl = (url) => {
      const u = new URL(url, window.location.origin);
      u.searchParams.delete('section_id');
      u.searchParams.delete('page');
      [...u.searchParams.keys()].forEach((k) => { if (u.searchParams.get(k) === '') u.searchParams.delete(k); });
      return u.pathname + (u.search ? u.search : '');
    };
    const render = async (url, push = true) => {
      ctrl && ctrl.abort();
      ctrl = new AbortController();
      root.classList.add('is-loading');
      try {
        const res = await fetch(withSection(url), { signal: ctrl.signal });
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.getElementById(root.id);
        if (!fresh) throw new Error('no section');
        const swap = (sel) => {
          const a = root.querySelector(sel), b = fresh.querySelector(sel);
          if (a && b) a.innerHTML = b.innerHTML;
        };
        const body = root.querySelector('[data-zjg-drawer-body]');
        const scrollTop = body ? body.scrollTop : 0;
        swap('[data-zjg-results]');
        swap('[data-zjg-drawer-body]');
        swap('.zjg-drawer__foot');
        const olds = root.querySelectorAll('[data-zjg-swap]'), news = fresh.querySelectorAll('[data-zjg-swap]');
        olds.forEach((el, i) => { if (news[i]) el.innerHTML = news[i].innerHTML; });
        if (body) body.scrollTop = scrollTop;
        if (push) window.history.pushState({ zjg: true }, '', url);
      } catch (e) {
        if (e.name !== 'AbortError') window.location.href = url;
      } finally {
        root.classList.remove('is-loading');
      }
    };

    const formUrl = () => {
      const params = new URLSearchParams();
      new FormData(form).forEach((v, k) => { if (String(v).trim() !== '') params.append(k, v); });
      const q = params.toString();
      return baseUrl + (q ? `?${q}` : '');
    };
    let t = 0;
    const apply = (delay = 0) => { clearTimeout(t); t = setTimeout(() => render(formUrl()), delay); };

    /* ---------- price helpers ---------- */
    const priceBox = () => root.querySelector('[data-zjg-price]');
    const syncRange = () => {
      const box = priceBox();
      if (!box) return;
      const max = Number(box.dataset.max) || 1;
      const lo = box.querySelector('[data-zjg-range="min"]'), hi = box.querySelector('[data-zjg-range="max"]');
      let a = Number(lo.value), b = Number(hi.value);
      if (a > b) [a, b] = [b, a];
      box.querySelector('.zjg-range').style.setProperty('--min', `${(a / max) * 100}%`);
      box.querySelector('.zjg-range').style.setProperty('--max', `${(b / max) * 100}%`);
      box.querySelector('[data-zjg-min]').value = a > 0 ? a : '';
      box.querySelector('[data-zjg-max]').value = b < max ? b : '';
    };

    /* ---------- events (delegated so they survive re-renders) ---------- */
    root.addEventListener('click', (e) => {
      const open = e.target.closest('[data-zjg-open]');
      if (open) { openDrawer(open); return; }
      if (e.target.closest('[data-zjg-close]')) { closeDrawer(); return; }
      const dens = e.target.closest('[data-zjg-density]');
      if (dens) { setDensity(dens.dataset.zjgDensity); return; }
      const tier = e.target.closest('[data-zjg-tier]');
      if (tier) {
        const [lo, hi] = tier.dataset.zjgTier.split('-');
        const box = priceBox();
        box.querySelector('[data-zjg-min]').value = Number(lo) > 0 ? lo : '';
        box.querySelector('[data-zjg-max]').value = hi || '';
        apply();
        return;
      }
      const link = e.target.closest('[data-zjg-link]');
      if (link && link.href && !link.target) { e.preventDefault(); render(cleanUrl(link.href)); return; }
      const more = e.target.closest('[data-zjg-more]');
      if (more) { e.preventDefault(); loadMore(more); }
    });
    root.addEventListener('change', (e) => {
      const el = e.target;
      if (el.matches('[data-zjg-sort]')) {
        const radio = form.querySelector(`input[name="sort_by"][value="${CSS.escape(el.value)}"]`);
        form.querySelectorAll('input[name="sort_by"]').forEach((r) => { r.checked = false; });
        let hidden = form.querySelector('input[type="hidden"][name="sort_by"]');
        if (radio) { radio.checked = true; if (hidden) hidden.remove(); }
        else { if (!hidden) { hidden = document.createElement('input'); hidden.type = 'hidden'; hidden.name = 'sort_by'; form.appendChild(hidden); } hidden.value = el.value; }
        apply();
        return;
      }
      if (!form.contains(el)) return;
      if (el.matches('[data-zjg-range]')) { syncRange(); apply(); return; }
      if (el.matches('input[type="checkbox"], input[type="radio"]')) { apply(); return; }
      if (el.matches('[data-zjg-min], [data-zjg-max]')) apply(300);
    });
    root.addEventListener('input', (e) => { if (e.target.matches('[data-zjg-range]')) syncRange(); });
    form.addEventListener('submit', (e) => { e.preventDefault(); closeDrawer(); render(formUrl()); root.querySelector('[data-zjg-results]').scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }); });
    window.addEventListener('popstate', () => render(window.location.pathname + window.location.search, false));

    /* ---------- load more ---------- */
    const loadMore = async (btn) => {
      if (btn.classList.contains('is-loading')) return;
      btn.classList.add('is-loading');
      const label = btn.querySelector('[data-zjg-more-label]');
      if (label) label.textContent = 'Loading';
      try {
        const res = await fetch(withSection(btn.href));
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.getElementById(root.id);
        const grid = root.querySelector('[data-zjg-grid]');
        const items = fresh ? [...fresh.querySelectorAll('[data-zjg-grid] > [data-zjg-item]')] : [];
        items.forEach((li, i) => { li.classList.add('is-new'); li.style.animationDelay = `${Math.min(i, 8) * 40}ms`; grid.appendChild(li); });
        const wrap = root.querySelector('[data-zjg-more-wrap]');
        const freshWrap = fresh && fresh.querySelector('[data-zjg-more-wrap]');
        if (wrap && freshWrap) wrap.innerHTML = freshWrap.innerHTML;
      } catch (e) {
        window.location.href = btn.href;
      }
    };
  };

  const boot = (scope) => scope.querySelectorAll('[data-zjg]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document));
  else boot(document);
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
